import { useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical, Tag as TagIcon } from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { PageHeader } from '../components/ui/responsive-table';
import {
  useTags,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
} from '../hooks/useTags';
import type { Tag, CreateTagInput, UpdateTagInput } from '../services/tags';

export default function Tags() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: tagsData, isLoading } = useTags();
  const createTag = useCreateTag();
  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();

  const tags = tagsData || [];

  // Form state
  const [formData, setFormData] = useState<CreateTagInput>({
    name: '',
    slug: '',
    color: '#9333EA',
    order: 0,
  });

  const handleOpenDialog = (tag?: Tag) => {
    if (tag) {
      setEditingTag(tag);
      setFormData({
        name: tag.name,
        slug: tag.slug,
        color: tag.color || '#9333EA',
        order: tag.order,
      });
    } else {
      setEditingTag(null);
      setFormData({
        name: '',
        slug: '',
        color: '#9333EA',
        order: tags.length,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTag(null);
    setFormData({
      name: '',
      slug: '',
      color: '#9333EA',
      order: 0,
    });
  };

  const handleNameChange = (name: string) => {
    // Auto-generate slug from name
    const slug = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

    setFormData({ ...formData, name, slug });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingTag) {
      await updateTag.mutateAsync({
        id: editingTag.id,
        data: formData as UpdateTagInput,
      });
    } else {
      await createTag.mutateAsync(formData);
    }

    handleCloseDialog();
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      await deleteTag.mutateAsync(id);
      setDeleteConfirmId(null);
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Tags"
          description="Gérer les tags des articles de blog"
          action={
            <Button onClick={() => handleOpenDialog()} className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Nouveau tag</span>
              <span className="sm:hidden">Nouveau</span>
            </Button>
          }
        />

        {/* Mobile/Tablet: Card Layout */}
        <div className="lg:hidden space-y-4">
          {tags.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground bg-white rounded-lg border">
              Aucun tag. Créez-en un pour commencer.
            </div>
          ) : (
            tags.map((tag) => (
              <div
                key={tag.id}
                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Color indicator */}
                    <div
                      className="w-12 h-12 rounded-lg border flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: tag.color || '#9333EA' }}
                    >
                      <TagIcon className="h-6 w-6 text-white" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{tag.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {tag._count?.articles || 0} articles
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">/{tag.slug}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <GripVertical className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-gray-400">Ordre: {tag.order}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(tag)}
                      className="flex-1 sm:flex-none"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(tag.id)}
                      className={`${deleteConfirmId === tag.id ? 'text-red-600 bg-red-50' : 'text-red-600 hover:text-red-700 hover:bg-red-50'}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      {deleteConfirmId === tag.id && <span className="ml-2">Confirmer</span>}
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop: Table Layout */}
        <div className="hidden lg:block border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Ordre</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Couleur</TableHead>
                <TableHead className="text-center">Articles</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Aucun tag. Créez-en un pour commencer.
                  </TableCell>
                </TableRow>
              ) : (
                tags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                        <span className="text-sm text-muted-foreground">{tag.order}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <TagIcon className="h-4 w-4" style={{ color: tag.color || '#9333EA' }} />
                        {tag.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">{tag.slug}</code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border"
                          style={{ backgroundColor: tag.color || '#9333EA' }}
                        />
                        <code className="text-xs text-muted-foreground">
                          {tag.color || '#9333EA'}
                        </code>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">
                        {tag._count?.articles || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(tag)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(tag.id)}
                          className={deleteConfirmId === tag.id ? 'text-red-600' : ''}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl mx-4 sm:mx-auto">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl">
                  {editingTag ? 'Modifier le tag' : 'Nouveau tag'}
                </DialogTitle>
                <DialogDescription>
                  {editingTag ? 'Modifiez les informations du tag.' : 'Créez un nouveau tag pour catégoriser vos articles.'}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-3 sm:py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Bien-être"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="bien-etre"
                    pattern="[a-z0-9\-]+"
                    title="Lettres minuscules, chiffres et tirets uniquement"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Lettres minuscules, chiffres et tirets uniquement
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="color">Couleur</Label>
                    <div className="flex gap-2">
                      <Input
                        id="color"
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-16 h-10 p-1"
                      />
                      <Input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        placeholder="#9333EA"
                        pattern="^#[0-9A-Fa-f]{6}$"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="order">Ordre d'affichage</Label>
                    <Input
                      id="order"
                      type="number"
                      min="0"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={createTag.isPending || updateTag.isPending}
                >
                  {editingTag ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
